
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { 
  Settings,
  ShieldCheck,
  User,
  ChevronRight,
  Crown
} from "lucide-react";

/**
 * MobileMenuAccount Component
 * 
 * Account-related navigation items (profile, settings, admin).
 * Phase 3.1: Enhanced with improved visual design and admin badge
 * 
 * Features:
 * - 56px minimum height for accessibility
 * - Color-coded icon backgrounds
 * - Special styling for admin panel (gradient background, shine effect)
 * - Crown badge for admin users
 * - Animated shine effect on admin button
 * - Wiggle animation on icon hover
 * 
 * @param {MobileMenuAccountProps} props - Component props
 * @returns {JSX.Element} Account navigation items
 */

interface AuthUser {
  role?: string;
}

const itemVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
  },
};

interface MobileMenuAccountProps {
  user: AuthUser;
  handleLinkClick: (path: string) => void;
}

const MobileMenuAccount = ({ user, handleLinkClick }: MobileMenuAccountProps) => {
  const isAdmin = user.role === 'admin';

  return (
    <>
      <motion.div variants={itemVariants}>
        <motion.button 
          onClick={() => handleLinkClick('/profile')} 
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          className="w-full text-left flex justify-between items-center min-h-[56px] px-4 py-3 rounded-2xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent hover:border-border/40 transition-all duration-300 group hover:shadow-sm"
        >
          <div className="flex items-center gap-4">
            <motion.div
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.3 }}
              className="p-2 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors duration-300"
            >
              <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </motion.div>
            <span className="font-semibold">Profile</span>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary transition-all duration-300" />
        </motion.button>
      </motion.div>

      <motion.div variants={itemVariants}>
        <motion.button 
          onClick={() => handleLinkClick('/settings')} 
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          className="w-full text-left flex justify-between items-center min-h-[56px] px-4 py-3 rounded-2xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent hover:border-border/40 transition-all duration-300 group hover:shadow-sm"
        >
          <div className="flex items-center gap-4">
            <motion.div
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.3 }}
              className="p-2 rounded-xl bg-slate-500/10 group-hover:bg-slate-500/20 transition-colors duration-300"
            >
              <Settings className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </motion.div>
            <span className="font-semibold">Settings</span>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary transition-all duration-300" />
        </motion.button>
      </motion.div>

      {isAdmin && (
        <motion.div variants={itemVariants}>
          <motion.button 
            onClick={() => handleLinkClick('/admin')} 
            whileHover={{ x: 4, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className="w-full text-left flex justify-between items-center min-h-[56px] px-4 py-3 rounded-2xl text-base font-medium bg-gradient-to-r from-amber-500/15 to-orange-500/15 text-foreground hover:from-amber-500/25 hover:to-orange-500/25 border border-amber-500/30 hover:border-amber-500/50 transition-all duration-300 group hover:shadow-lg relative overflow-hidden"
          >
            {/* Animated shine effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
              animate={{
                x: ['-100%', '100%'],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 5,
              }}
            />
            
            <div className="flex items-center gap-4 relative z-10">
              <motion.div
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.3 }}
                className="p-2 rounded-xl bg-amber-500/20 group-hover:bg-amber-500/30 transition-colors duration-300"
              >
                <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </motion.div>
              <div className="flex items-center gap-2">
                <span className="font-bold">Admin Panel</span>
                <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs">
                  <Crown className="h-3 w-3 mr-1" />
                  VIP
                </Badge>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-amber-600 dark:text-amber-400 relative z-10" />
          </motion.button>
        </motion.div>
      )}
    </>
  );
};

export default MobileMenuAccount;
